// Live-tunable physics + game-feel constants for the virtual cornhole
// game. The renderer and the gameplay loop read from a single source
// of truth here; the /admin/physics page mutates it and the changes
// take effect on the next frame (no rebuild, no restart).
//
// Persistence is server-side now (table `physics_tuning`, single row
// "default"). This module owns ONLY the in-memory singleton — the
// React Query sync layer (`PhysicsTuningSync` in App.tsx) seeds the
// singleton from the server on boot and on cache invalidation, and
// the tuner UI pushes patches back via useUpdatePhysicsTuning().
// We kept the singleton + subscribe API because the renderer reads
// getTuning() synchronously every frame and we don't want to thread
// React Query into the canvas loop.

import { useEffect, useState } from "react";

export type GhostBias = {
  aimSigma: number; // stddev of lateral aim noise (0..1)
  powerMs: number; // mean swipe-power equivalent
  powerJitter: number; // stddev of power noise (ms)
  loft: number; // mean vertical aim (0..1)
};

export type PhysicsTuning = {
  // World
  gravity: number; // m/s² (negative = downward)

  // Slab sliding physics — read each frame inside createBag.slideStep().
  frictionMu: number; // 0..1, bag-on-board friction coefficient
  bagInelasticY: number; // 0..1, fraction of horizontal velocity kept on landing
  bagSettleThreshold: number; // m/s, below this the bag stops sliding
  bagEdgeTolerance: number; // multiplier on bag.size for slab edge slack
  // Lip capture — meters of rim a resting bag may hang over and still
  // tip into the hole. A bag that comes to rest with its center this
  // far (or less) PAST the hole rim drops in instead of perching on
  // the edge: real bean bags draped over a hole fall through. 0
  // disables (bag must land fully inside the rim to score 3). The
  // bag's slide barely moves it after landing, so without this a
  // throw "heading for the hole" that lands a few cm short scores 1
  // — this reclaims those near-misses as the 3 they were headed for.
  holeLipCapture: number;

  // Throw aim — read by createVirtualGame.throwBag().
  targetZFraction: number; // 0..1, where on the slab a perfect throw lands (0=front edge, 0.5=center, 1=back edge)
  flightTimeBase: number; // seconds, flight time at zero power
  flightTimeFactor: number; // subtracted from flightTimeBase per unit power (0..1)
  vyLookahead: number; // extra Y above target (gives arc clearance)
  vyDirYFactor: number; // multiplier on user's loft input
  vxFactor: number; // multiplier on user's lateral aim
  vzDirXFactor: number; // adds curve from lateral aim
  // Distance scaling — TWO ends of a ramp that maps swipe power to
  // how far the bag actually travels. distanceScale =
  //   powerDistanceFloor + (powerDistanceCeiling − powerDistanceFloor) × power.
  // power=0  → bag lands at floor × (start → ideal target) distance
  // power=1  → bag lands at ceiling × (start → ideal target) distance
  // Set ceiling > 1.0 to let max-power swipes OVERSHOOT the board.
  powerDistanceFloor: number;
  powerDistanceCeiling: number;

  // Power range — applied to user's swipe-derived ms duration.
  powerMinMs: number;
  powerMaxMs: number;

  // Finger-throw mode: the player's bag pops at the bottom of the
  // screen for direct finger-flick throws. The toggle is now
  // permanently on (the rig system was removed) but the field stays
  // on the type so old localStorage tuning blobs keep parsing through
  // deepMergeTuning. Renderer never reads it anymore.
  useFingerThrow: number;
  fingerBagOffsetX: number; // camera-local x, 0 = centered
  fingerBagOffsetY: number; // camera-local y, negative = lower
  fingerBagOffsetZ: number; // camera-local z, more negative = farther
  fingerBagScale: number; // 1 = default size, 1.5+ for "easier to touch"
  fingerBagRotX: number; // pitch (degrees) — tilt the bag top toward camera
  fingerBagRotY: number; // yaw — rotate the bag horizontally
  fingerBagRotZ: number; // roll — spin the bag in the screen plane

  // Yaw spin rate (rad/s) applied to in-flight + sliding bags. Stays
  // constant through both phases so the bag keeps gently rotating
  // even after it lands. Negative = CLOCKWISE from above (which is
  // how a flicked bag actually spins when thrown sidearm).
  // Per-throw jitter of ±30% around this rate keeps multiple bags
  // from looking mechanical.
  bagYawSpinRate: number;

  // World-bag visual scale — multiplied onto the IN-FLIGHT bag and
  // PILE bag meshes only. The finger bag has its own scale knob;
  // the geometric BAG_GEOMETRY.size (now ~0.13m) reads small
  // against the 1.22m-wide far board, so this lets the admin pump
  // visible size without changing the underlying physics
  // footprint. Default 1 = real proportions; ~1.6 to ~2.0 reads
  // closer to the proportions you'd see watching a real game.
  bagWorldScale: number;

  // GLOBAL bag geometry. The renderer used to read .size and
  // .thickness from each BagAsset, which let per-asset metadata
  // diverge between team-1 and team-2 picks — a player would see
  // their own bag at one size and the opponent's at a different
  // size, breaking the "everyone sees the same world" invariant.
  // These knobs are the single source of truth for bag dimensions
  // across every device + game; texture/color/roughness remain
  // per-asset. 0.095m square × 0.0125m thick matches a real
  // regulation cornhole bag at our world scale.
  bagSize: number;
  bagThickness: number;

  // Landed-bag pitch OFFSET in degrees. The renderer auto-tilts the
  // bag to match the board's rake when it lands (so it sits flush,
  // not partially through the slab). This knob lets the admin
  // fine-tune that pitch ±a few degrees for cases where the auto-
  // computed angle still reads slightly off. 0 (default) = exact
  // match to board rake.
  bagLandedPitchOffsetDeg: number;

  // (Bag-texture transform USED to live here as four global knobs;
  // it's now per-asset on the BagAsset itself — edited via /admin/
  // design's bag alignment tool and stored in the row's metadata.
  // See applyBagTextureTransform in virtual3d.ts.)

  // Camera (rebuilt on tuning change inside createVirtualGame).
  cameraLookAtY: number;
  // Camera field of view in degrees. Lower values compress
  // perspective (the "telephoto" / zoom-in effect) so distant
  // objects appear larger relative to nearby ones — this is the
  // dial for "make the far board feel closer without moving the
  // camera". 55° (the cinematic default) is wide; 40-45° pulls
  // the far board in noticeably. 30° goes near-orthographic.
  cameraFov: number;

  // Lighting intensities.
  ambientIntensity: number;
  hemiIntensity: number;
  sunIntensity: number;
  // Directional sun position in world space. The light shines from
  // this point toward the world origin (0,0,0). Together they
  // define the sun direction: a higher Y is more overhead, X/Z
  // skew the light toward one side. The directional light's shadow
  // camera follows this position each frame, so shadows update
  // live when these dials change.
  sunPositionX: number;
  sunPositionY: number;
  sunPositionZ: number;

  // How many times the ground texture tiles across the 60m ground
  // plane. 20 = tight grass-blade tile (built-in). Drop to 1 to use
  // a single image as a one-shot backdrop (good for panoramic scene
  // photos). Applied to the scene's image when one is present.
  groundTileCount: number;

  // (Scene backdrop framing USED to live here as global knobs.
  // Moved per-scene-asset in 2026-05 because no single set of
  // values reasonably fits every generated photo. See SceneAsset
  // .backdropDistanceZ/OffsetY/Width/Height/Scale/OffsetU/OffsetV
  // and the Align Scene modal in /admin/design.)

  // Per-level Ghost AI biases.
  ghostBias: Record<string, GhostBias>;
};

export const DEFAULT_TUNING: PhysicsTuning = {
  gravity: -9.81,

  frictionMu: 0.32,
  bagInelasticY: 0.65,
  bagSettleThreshold: 0.025,
  bagEdgeTolerance: 0.3,
  holeLipCapture: 0.06,

  targetZFraction: 0.166, // center of front 1/3
  flightTimeBase: 1.3,
  flightTimeFactor: 0.35,
  vyLookahead: 0.4,
  vyDirYFactor: 1.6,
  vxFactor: 5.5,
  vzDirXFactor: 0.4,
  powerDistanceFloor: 0.4,
  powerDistanceCeiling: 1.25,

  powerMinMs: 150,
  powerMaxMs: 1100,

  useFingerThrow: 1,
  fingerBagOffsetX: 0,
  fingerBagOffsetY: -0.35,
  fingerBagOffsetZ: -1.1,
  fingerBagScale: 1.3,
  fingerBagRotX: -45,
  fingerBagRotY: 0,
  fingerBagRotZ: 0,

  bagYawSpinRate: -0.55,

  // Match the regulation 6×6×~0.5 inch bag at the renderer's world
  // scale. Identical to the previous BAG_GEOMETRY constant; admins
  // can dial up/down from /admin/physics now.
  bagSize: 0.095,
  bagThickness: 0.0125,

  // Matches fingerBagScale by default so the player's held bag and
  // the opponent's at-rest bag (and the player's own bag once it
  // launches) read as the same physical object. Two independent
  // scales used to drift apart visually; keep them aligned unless
  // the tuner explicitly wants them different.
  bagWorldScale: 1.3,
  bagLandedPitchOffsetDeg: 0,

  cameraLookAtY: 0.3,
  cameraFov: 55,

  ambientIntensity: 0.15,
  hemiIntensity: 0.85,
  sunIntensity: 1.1,
  // Default sun: high right-front, casts shadows toward back-left.
  sunPositionX: 6,
  sunPositionY: 10,
  sunPositionZ: 4,

  groundTileCount: 20,

  ghostBias: {
    "ghost-2": { aimSigma: 0.55, powerMs: 1100, powerJitter: 380, loft: 0.55 },
    "ghost-3": { aimSigma: 0.32, powerMs: 1150, powerJitter: 230, loft: 0.5 },
    "ghost-4": { aimSigma: 0.18, powerMs: 1180, powerJitter: 140, loft: 0.48 },
    "ghost-5": { aimSigma: 0.09, powerMs: 1200, powerJitter: 80, loft: 0.46 },
  },
};

// Legacy storage key — kept here ONLY so the boot-time cleanup in
// PhysicsTuningSync knows which key to remove. No write path uses it
// anymore.
export const LEGACY_STORAGE_KEY = "cornholio.physicsTuning";

// Deep-merge a partial against an existing tuning. Used both for
// applying user patches AND for hydrating server data (which arrives
// as an opaque blob — typically a subset of PhysicsTuning) into the
// in-memory singleton. Special-cases `ghostBias` so per-level patches
// don't clobber sibling levels.
export function deepMergeTuning(
  base: PhysicsTuning,
  patch: Partial<PhysicsTuning>,
): PhysicsTuning {
  const out: PhysicsTuning = { ...base, ...patch };
  if (patch.ghostBias) {
    out.ghostBias = { ...base.ghostBias };
    for (const [k, v] of Object.entries(patch.ghostBias)) {
      out.ghostBias[k] = { ...(base.ghostBias[k] ?? {}), ...v } as GhostBias;
    }
  }
  return out;
}

// In-memory singleton. Initialized to defaults; PhysicsTuningSync
// overlays the server blob on top once it loads. Renderer reads this
// synchronously every frame via getTuning().
let current: PhysicsTuning = { ...DEFAULT_TUNING };
const subscribers = new Set<() => void>();

export function getTuning(): PhysicsTuning {
  return current;
}

// Local-only setter — updates the singleton + notifies subscribers
// (the React UI re-renders). Server persistence is the caller's job
// (PhysicsTuner's onChange ALSO fires useUpdatePhysicsTuning().mutate).
export function setTuning(patch: Partial<PhysicsTuning>): void {
  current = deepMergeTuning(current, patch);
  for (const fn of subscribers) fn();
}

export function setGhostBias(level: string, patch: Partial<GhostBias>): void {
  setTuning({
    ghostBias: {
      ...current.ghostBias,
      [level]: { ...current.ghostBias[level]!, ...patch },
    },
  });
}

export function resetTuning(): void {
  current = { ...DEFAULT_TUNING };
  for (const fn of subscribers) fn();
}

export function subscribeTuning(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

// React hook for the admin UI. Re-renders on every setTuning call.
export function usePhysicsTuning(): PhysicsTuning {
  const [t, setT] = useState<PhysicsTuning>(current);
  useEffect(() => {
    const unsub = subscribeTuning(() => setT(getTuning()));
    return unsub;
  }, []);
  return t;
}
